import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { MatrixData, MatrixRowValue, Row } from '../components/matrix';
import { rowValueIsDifferent } from 'app/b2b-order/components/matrix/helpers';
import { Dictionary, GtinMap } from 'types';

@Injectable()
export class MatrixClipboardService {
    public readonly hasCopiedValue: Observable<boolean>;

    private readonly hasCopiedValueSubject = new BehaviorSubject(false);
    private copiedValue: MatrixData = null;

    constructor() {
        this.hasCopiedValue = this.hasCopiedValueSubject.asObservable();
    }

    public copy(matrixData: MatrixData) {
        if (!matrixData) {
            return;
        }

        this.hasCopiedValueSubject.next(true);
        this.copiedValue = matrixData;
    }

    public paste(destinationMatrixData: MatrixData): MatrixRowValue[] {
        if (!this.copiedValue) {
            return [];
        }

        const copiedSizeMap = this.copiedValue.headers.reduce<Dictionary<string, 'size'>>(
            (sizeMap, { text: size, key: gtin }) => ({ ...sizeMap, [size]: gtin }),
            {}
        );
        const destinationGtinMap = destinationMatrixData.headers.reduce<GtinMap>(
            (gtinMap, { text: size, key: gtin }) => ({ ...gtinMap, [gtin]: copiedSizeMap[size] }),
            {}
        );

        const activeMatrixRows = this.getActiveMatrixRows(destinationMatrixData);
        return this.calculateUpdatedRows(activeMatrixRows, destinationGtinMap, this.copiedValue.dataSource);
    }

    public clear() {
        this.copiedValue = null;
        this.hasCopiedValueSubject.next(false);
    }

    private calculateUpdatedRows(
        activeMatrixRows: { dataSourceRow: Row; rowIndex: number }[],
        destinationGtinMap: GtinMap,
        copiedDataSource: Row[]
    ) {
        return activeMatrixRows.map(({ dataSourceRow, rowIndex }) => {
            const copiedValueRowCells = copiedDataSource[rowIndex].cells;
            const row = _.reduce(
                dataSourceRow.cells,
                (updatedDestinationRow, destinationCell, destinationGtin) => {
                    const copiedValueGtin = destinationGtinMap[destinationGtin];
                    const copiedValueCell = copiedValueRowCells[copiedValueGtin];
                    return {
                        ...updatedDestinationRow,
                        [destinationGtin]: (copiedValueCell?.value ?? destinationCell.value).toString()
                    };
                },
                {} as GtinMap
            );

            return {
                rowIndex,
                row,
                hasChanges: rowValueIsDifferent({ row }, dataSourceRow)
            };
        });
    }

    private getActiveMatrixRows(matrixData: MatrixData) {
        return matrixData.dataSource
            .map((dataSourceRow, rowIndex) => ({ dataSourceRow, rowIndex }))
            .filter(
                ({ dataSourceRow, rowIndex }) =>
                    !_.some(
                        dataSourceRow.cells,
                        ({ inputOptions }) => inputOptions?.disabled || inputOptions?.blocked
                    ) && this.copiedValue.dataSource[rowIndex]
            );
    }
}
